import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text, useTheme } from '@Sage/hoi-poi-ui';

import EntityLink from 'components/EntityLink';
import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { canAccessEmailCampaigns } from 'containers/campaigns/utils/campaignPermissions';
import { logMarketingCampaignsEvent } from 'containers/campaigns/marketing/utils/marketingTracking';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import { getLiteralWithParametersHtml } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { stopEntityNavigation } from 'utils/entityNavigation';

import {
    parseMarketingStatusHistoryLabel,
    resolveMarketingStatusHistoryLabel,
} from './helpers/marketingStatusHistoryLabel';

const MarketingStatusHistorySourceValue = memo(({ sourceLabel }) => {
    const theme = useTheme();
    const canAccessCampaigns = useSelector(canAccessEmailCampaigns);
    const { hasTabsEnabled, openTab } = useEntityDetail();

    const parsed = useMemo(() => parseMarketingStatusHistoryLabel(sourceLabel), [sourceLabel]);

    const handleCampaignClick = useCallback(
        (campaignId) => (event) => {
            if (campaignId == null) {
                return stopEntityNavigation(event);
            }

            logMarketingCampaignsEvent({
                submodule: 'marketingStatus',
                functionality: 'historySourceCampaignLink',
            });

            event.preventDefault();
            event.stopPropagation();

            if (hasTabsEnabled) {
                openTab({
                    entity: CAMPAIGNS_MARKETING,
                    id: campaignId,
                    avoidRedirects: true,
                });
                return;
            }

            ensureRoute(`${CAMPAIGNS_MARKETING.route}/${campaignId}`);
        },
        [hasTabsEnabled, openTab],
    );

    const customizeParameter = useCallback(
        (value, index) => {
            const ref = parsed?.refs?.[index];
            const entity = ref?.entity;
            const entityId = ref?.id;

            if (!canAccessCampaigns || entity !== CAMPAIGNS_MARKETING || entityId == null) {
                return (
                    <Text type="caption" color={theme.colors.utility.textPrimary}>
                        {value}
                    </Text>
                );
            }

            const href = `${CAMPAIGNS_MARKETING.route}/${entityId}`;

            return (
                <EntityLink
                    entity={entity}
                    href={href}
                    onClick={handleCampaignClick(entityId)}
                    linkProps={{
                        type: 'caption',
                        className: 'fm-marketing-status-history-modal__source-link',
                        variation: 'primary',
                        underline: true,
                    }}
                >
                    {value}
                </EntityLink>
            );
        },
        [canAccessCampaigns, handleCampaignClick, parsed?.refs, theme.colors.utility.textPrimary],
    );

    const content = useMemo(() => {
        if (!parsed?.key) {
            return null;
        }

        if (!parsed.values.length) {
            return resolveMarketingStatusHistoryLabel(sourceLabel);
        }

        return getLiteralWithParametersHtml(parsed.key, parsed.values, customizeParameter);
    }, [customizeParameter, parsed, sourceLabel]);

    if (content == null || content === '') {
        return null;
    }

    if (typeof content === 'string') {
        return (
            <Text type="caption" color={theme.colors.utility.textPrimary}>
                {content}
            </Text>
        );
    }

    return (
        <Text
            type="caption"
            color={theme.colors.utility.textPrimary}
            className="fm-marketing-status-history-modal__source-value"
        >
            {content}
        </Text>
    );
});

MarketingStatusHistorySourceValue.displayName = 'MarketingStatusHistorySourceValue';

export default MarketingStatusHistorySourceValue;
